import { adaptPreview } from '../service/adaptPreview'
import { classifyInsightActionError, isCanceled } from '../service/classifyInsightActionError'
import { previewKey, type RunState } from '../stores/insightActionStore'

import type {
  InsightActionApiSlice,
  InsightActionScope,
  InsightActionStoreSlice,
} from './insightActionFlow.types'

export interface LoadPreviewFlowDeps {
  api: InsightActionApiSlice<'preview'>
  store: InsightActionStoreSlice<
    | 'cached'
    | 'setPreviewLoading'
    | 'setPreview'
    | 'setPreviewError'
    | 'activeKey'
    | 'ensureGroup'
    | 'runs'
    | 'setRun'
  >
}

export interface LoadPreviewOptions {
  force?: boolean
  signal?: AbortSignal
}

export class LoadPreviewFlow {
  constructor(private readonly deps: LoadPreviewFlowDeps) {}

  async execute(scope: InsightActionScope, options: LoadPreviewOptions = {}): Promise<void> {
    const key = previewKey(scope.accountId, scope.workspaceId, scope.insightId)
    const cached = this.deps.store.cached(key)
    const settled = cached && (cached.loading || cached.preview !== null || cached.errorCode !== '')
    if (settled && options.force !== true) return

    this.deps.store.setPreviewLoading(key)

    try {
      const dto = await this.deps.api.preview(
        scope.accountId,
        scope.workspaceId,
        scope.insightId,
        options.signal,
      )
      const preview = adaptPreview(dto)
      this.deps.store.setPreview(key, preview)
      if (key === this.deps.store.activeKey) this.deps.store.ensureGroup()
      this.adoptActiveRun(key, scope.insightId, preview.activeRun)
    } catch (cause) {
      const error = classifyInsightActionError(cause)
      if (isCanceled(error)) return
      this.deps.store.setPreviewError(key, error.code)
    }
  }

  private adoptActiveRun(
    key: string,
    insightId: number,
    activeRun: {
      runId: string
      status: RunState['status']
      totalItems: number
      counts: RunState['counts']
    } | null,
  ): void {
    if (!activeRun || this.deps.store.runs[key]) return
    this.deps.store.setRun(key, {
      runId: activeRun.runId,
      insightId,
      status: activeRun.status,
      totalItems: activeRun.totalItems,
      counts: activeRun.counts,
      detail: null,
      items: [],
    })
  }
}
